// Een fasekaart op het bord (ontwerp "Fases op het bord"): de fase uit Planner als kaart, met de taken van deze kolom
// erin en hoogstens twee fasekaarten in elkaar (zie lib/fases.ts). De kop opent de faselade, het pijltje klapt de kaart
// in. Een div en geen knop, want er zitten taakknoppen in. De kaart zelf sleept niet: alle taken in één keer
// hertoewijzen is bewust niet gebouwd; de taken erin slepen wel, en behouden hun fase.
import type { ReactNode } from "react";
import { catOf, labelColor } from "../lib/board";
import type { FaseBlock } from "../lib/fases";
import { Icon } from "../lib/icons";
import { ddmm, deadline } from "../lib/planning";
import type { Task } from "../lib/types";

export interface FaseCardProps {
  today: string;
  /** open taken van deze fase die niet in deze kolom staan; enkel op een kaart bovenaan */
  others: (faseId: string) => number;
  isOpen: (faseId: string) => boolean;
  onToggle: (faseId: string) => void;
  onOpenFase: (faseId: string) => void;
  renderTask: (t: Task) => ReactNode;
}

export function FaseCard({ block, ...p }: FaseCardProps & { block: FaseBlock }) {
  const { fase, pad, tasks, children, top } = block;
  const open = p.isOpen(fase.id);
  const d = deadline(fase.end, p.today);
  const pct = fase.total ? Math.round((fase.done / fase.total) * 100) : 0;
  const others = top ? p.others(fase.id) : 0;
  const late = tasks.filter((t) => catOf(t) === "actief" && t.end < p.today).length;
  const next = tasks.filter((t) => t.end >= p.today).map((t) => t.end).sort()[0];
  const n = (k: number) => (k === 1 ? "1 taak" : k + " taken");
  return (
    <div className="p-fk" data-fase={fase.id}>
      <div className="p-fk-h">
        <button type="button" className="p-fk-open" title={"Fase openen: " + [...pad, fase.title].join(" › ")} onClick={() => p.onOpenFase(fase.id)}>
          {pad.length > 0 && <span className="p-fk-pad">{pad.join(" › ")}</span>}
          <span className="p-fk-t">{fase.title}</span>
          <span className="p-fk-meta">
            <span className="p-fk-chip"><Icon name="fase" size={11} />Fase</span>
            <span className={"p-dl" + (d.tone ? " t-" + d.tone : "")}><Icon name={d.key === "verlopen" ? "alert" : "cal"} size={13} />{d.text}</span>
            <span className="p-fk-prog" title={fase.done + " van " + fase.total + " taken af"}><i><b style={{ width: pct + "%" }} /></i>{fase.done}/{fase.total} af</span>
            {fase.labels.map((l) => <span key={l.id} className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span>)}
          </span>
        </button>
        <button type="button" className="p-fk-tog" aria-expanded={open} aria-label={(open ? "Inklappen: " : "Openklappen: ") + fase.title} title={n(tasks.length) + " in deze kolom"} onClick={() => p.onToggle(fase.id)}>
          {tasks.length}<Icon name={open ? "down" : "right"} size={14} />
        </button>
      </div>
      {open ? <>
        {children.map((b) => (b.kind === "task" ? p.renderTask(b.task) : <FaseCard key={b.fase.id} block={b} {...p} />))}
        {others > 0 && <button type="button" className="p-fk-more" title="Opent de faselade" onClick={() => p.onOpenFase(fase.id)}>{n(others)} bij collega's</button>}
      </> : (
        <p className="p-fk-sum">{n(tasks.length)}{late ? <> · <b className="t-err">{late} te laat</b></> : next ? " · eerstvolgende " + ddmm(next) : ""}</p>
      )}
    </div>
  );
}
